import React from 'react';

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  gene: string;
  group: string;
  value: number | null;
}

interface GroupHeatmapProps {
  genes: string[];
  groups: string[];
  values: Record<string, Record<string, number | null>>;
  selectedGroup: string;
  setSelectedGroup: (g: string) => void;
  heatmapGroups: string[];
  tooltip: TooltipState;
  setTooltip: React.Dispatch<React.SetStateAction<TooltipState>>;
}

const GroupHeatmap: React.FC<GroupHeatmapProps> = ({
  genes,
  groups,
  values,
  selectedGroup,
  setSelectedGroup,
  heatmapGroups,
  tooltip,
  setTooltip
}) => {
  // Responsive sizing (must be before hooks)
  const cellSize = Math.max(16, Math.min(28, Math.floor(700 / Math.max(groups.length, 10))));
  const leftMargin = 200; // Increased from 150 to 200 to provide even more space for row labels
  const topMargin = 80; // Increased from 40 to 80 to add more space on top
  const labelFontSize = 13;
  const labelHeight = 32;
  // Expand svgWidth to allow for legend space
  const legendExtraWidth = 260;
  const svgWidth = cellSize * groups.length + leftMargin + 20 + legendExtraWidth;
  const svgHeight = cellSize * genes.length + topMargin + labelHeight + 30;

  // All hooks must be called before any early return
  const [tooltipState, setTooltipState] = React.useState<TooltipState>(tooltip);
  React.useEffect(() => {
    if (tooltip.visible) {
      setTooltipState(tooltip);
    }
  }, [tooltip]);

  // Memoize min/max calculation
  const [min, max] = React.useMemo(() => {
    let min = Infinity, max = -Infinity;
    genes.forEach(g => groups.forEach(gr => {
      const v = values[g] && values[g][gr];
      if (typeof v === 'number') {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }));
    return [min, max];
  }, [genes, groups, values]);

  // Memoize color function
  const color = React.useCallback((v: number | null) => {
    if (v == null) return '#f0f0f0';
    const t = (v - min) / (max - min || 1);
    if (isNaN(t)) return '#f0f0f0';
    const r = Math.round(255 * t);
    const g = Math.round(255 * (1 - Math.abs(t - 0.5) * 2));
    const b = Math.round(255 * (1 - t));
    return `rgb(${r},${g},${b})`;
  }, [min, max]);

  // Tooltip dwell timer
  const dwellTimer = React.useRef<number | null>(null);
  // Track which cell is being hovered
  const hoveredCell = React.useRef<{ gene: string; group: string } | null>(null);

  // Memoize group label elements
  const groupLabelElements = React.useMemo(() =>
    groups.map((gr, i) => (
      <g key={gr}>
        <text
          x={leftMargin + i * cellSize + cellSize / 2}
          y={topMargin + labelHeight - cellSize + 8}
          fontSize={labelFontSize}
          textAnchor="start"
          transform={`rotate(-45,${leftMargin + i * cellSize + cellSize / 2},${topMargin + labelHeight - cellSize + 8})`}
          style={{ pointerEvents: 'none', fontWeight: 500, fill: '#2a2a4a' }}
        >
          {gr}
        </text>
      </g>
    ))
  , [groups, cellSize, leftMargin, topMargin, labelFontSize, labelHeight]);

  // Memoize gene label elements
  const geneLabelElements = React.useMemo(() =>
    genes.map((g, i) => (
      <text key={g} x={leftMargin - 12} y={topMargin + labelHeight + i * cellSize + cellSize / 2 + 5} fontSize={13} textAnchor="end" style={{ fontWeight: 500, fill: '#2a2a4a' }}>{g}</text>
    ))
  , [genes, cellSize, leftMargin, topMargin, labelHeight]);

  // Memoize heatmap cell elements
  const cellElements = React.useMemo(() =>
    genes.flatMap((g, i) => groups.map((gr, j) => {
      const v = values[g] && values[g][gr];
      const cellX = leftMargin + j * cellSize;
      const cellY = topMargin + labelHeight + i * cellSize;
      const handleMouseEnter = () => {
        if (dwellTimer.current) window.clearTimeout(dwellTimer.current);
        hoveredCell.current = { gene: g, group: gr };
        dwellTimer.current = window.setTimeout(() => {
          if (hoveredCell.current && hoveredCell.current.gene === g && hoveredCell.current.group === gr) {
            setTooltip(t => ({ ...t, visible: true, x: cellX + cellSize + 6, y: cellY - 2, gene: g, group: gr, value: v }));
          }
        }, 50);
      };
      const handleMouseLeave = () => {
        if (dwellTimer.current) window.clearTimeout(dwellTimer.current);
        hoveredCell.current = null;
        setTooltip(t => ({ ...t, visible: false }));
      };
      return (
        <rect
          key={g + gr}
          x={cellX}
          y={cellY}
          width={cellSize - 1}
          height={cellSize - 1}
          rx={3}
          fill={color(v)}
          style={{ cursor: 'pointer', stroke: '#fff', strokeWidth: 0.5, transition: 'fill 0.2s' }}
          onMouseEnter={handleMouseEnter}
          onMouseMove={undefined}
          onMouseLeave={handleMouseLeave}
        />
      );
    }))
  , [genes, groups, values, cellSize, leftMargin, topMargin, labelHeight, color, setTooltip]);

  if (!genes.length || !groups.length) return null;

  return (
    <div style={{ marginTop: 40, width: '100%', background: '#f5f5fa', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.07)', padding: 18, position: 'relative' }}>
      <h2 style={{ fontSize: 22, margin: '10px 0 18px 0', color: '#2a2a4a' }}>
        Gene Expression Heatmap for Top 50 Genes in Selected Group
      </h2>
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label htmlFor="group-heatmap-group" style={{ fontWeight: 500, color: '#2a2a4a', fontSize: 15, marginRight: 8 }}>Select sample group for top 50 genes:</label>
        <select
          id="group-heatmap-group"
          value={selectedGroup}
          onChange={e => setSelectedGroup(e.target.value)}
          style={{
            fontSize: 15,
            padding: '6px 16px',
            borderRadius: 8,
            border: '1.5px solid #b0b6c3',
            background: '#f7f8fa',
            color: '#2a2a4a',
            fontWeight: 500,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            outline: 'none',
            transition: 'border 0.2s',
            marginLeft: 0,
            marginRight: 0,
            appearance: 'none',
            minWidth: 120,
            cursor: 'pointer',
          }}
        >
          {heatmapGroups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div style={{ width: '100%', position: 'relative', background: '#fff', borderRadius: 10, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', padding: 10 }}>
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{ display: 'block', width: svgWidth, minWidth: svgWidth, height: 'auto', maxWidth: '100%' }}
          onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
        >
          {/* Group labels above the heatmap */}
          {groupLabelElements}
          {/* Gene labels */}
          {geneLabelElements}
          {/* Cells with tooltip events */}
          {cellElements}
          {/* Color legend - now further to the right of column titles, but inside bounds */}
          <g>
            <defs>
              <linearGradient id="legend-gradient" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor={color(min)} />
                <stop offset="100%" stopColor={color(max)} />
              </linearGradient>
            </defs>
            <rect x={svgWidth - 200} y={18} width={160} height={18} rx={6} fill="url(#legend-gradient)" />
            <text x={svgWidth - 192} y={27+4} fontSize={13} fill="#fff" textAnchor="start" dominantBaseline="middle">{min.toFixed(2)}</text>
            <text x={svgWidth - 48} y={27+4} fontSize={13} fill="#fff" textAnchor="end" dominantBaseline="middle">{max.toFixed(2)}</text>
            <text x={svgWidth - 120} y={27+4} fontSize={13} fill="#fff" textAnchor="middle" dominantBaseline="middle">Value</text>
          </g>
        </svg>
        {/* Tooltip popup */}
        <div
          style={{
            display: tooltip.visible ? 'block' : 'none',
            position: 'absolute',
            left: tooltipState.x,
            top: tooltipState.y,
            background: 'rgba(30,30,30,0.97)',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 7,
            fontSize: 15,
            pointerEvents: 'none',
            zIndex: 10,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            fontWeight: 500
          }}
        >
          <div><b>Gene:</b> {tooltipState.gene}</div>
          <div><b>Sample:</b> {tooltipState.group}</div>
          <div><b>Value:</b> {tooltipState.value !== null && tooltipState.value !== undefined ? tooltipState.value : 'N/A'}</div>
        </div>
      </div>
      <div style={{ marginTop: 18, fontSize: 14, color: '#444', textAlign: 'center', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
        <b>Note:</b> This heatmap shows the expression values of the top 50 genes (ranked by expression in the selected group) across all sample groups. Each cell represents the expression value of a gene (row) in a sample group (column), colored from low (blue) to high (red) expression. The top 50 genes are determined by their ranking in the selected group, and the color scale is calculated based on the minimum and maximum values in the displayed data.
      </div>
    </div>
  );
};

export default GroupHeatmap;
